/**
 * Ask Leo talks to OpenRouter only through `/api/leo/chat`. The key stays on
 * the Vite server. The browser never chooses a model. The server allows
 * `:free` ids and a short cheap paid list.
 */

import {
  AUTHORING_QUESTION_TYPES,
  buildLeoQuestionDraftVariants,
  questionTitleFromLeadIn,
  type AuthoringQuestionType,
  type LeoQuestionBrief,
  type LeoQuestionDraftPatch,
  type LeoQuestionDraftVariant,
} from "@/lib/library-authoring"
import {
  LEO_OPENROUTER_CHAT_PATH,
  type LeoChatMessage,
} from "./leo-openrouter-shared"
import { choiceSetIsBroken } from "@/lib/library-ai-spotted"

export {
  LEO_CHEAP_MODELS,
  LEO_FREE_MODEL_FALLBACKS,
  LEO_OPENROUTER_CHAT_PATH,
  assertLeoAllowedModel,
  assertLeoFreeModel,
  resolveLeoChatModels,
  resolveLeoFreeModels,
  type LeoChatMessage,
  type LeoChatRole,
} from "./leo-openrouter-shared"

export const LEO_GENERIC_SYSTEM = [
  "You are Leo, an exam authoring assistant in Exxat.",
  "Answer in short, useful sentences for faculty who write assessment items.",
  "Do not use em dashes, en dashes, or a hyphen as a pause.",
  "If a value is missing, say None. Do not invent student or item IDs.",
].join(" ")

export function isLeoOpenRouterClientEnabled(): boolean {
  return import.meta.env.MODE !== "test"
}

export function sanitizeLeoCopy(text: string): string {
  return text
    .replace(/\s*[\u2014\u2013]\s*/g, ", ")
    .replace(/\s+/g, " ")
    .trim()
}

export function parseLeoOpenRouterJson<T>(raw: string): T | null {
  const trimmed = raw
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
  const start = trimmed.indexOf("{")
  const end = trimmed.lastIndexOf("}")
  if (start < 0 || end <= start) return null
  try {
    return JSON.parse(trimmed.slice(start, end + 1)) as T
  } catch {
    return null
  }
}

export async function askLeoOpenRouter(
  input: {
    messages: LeoChatMessage[]
    json?: boolean
  },
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetch(LEO_OPENROUTER_CHAT_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    signal,
  })
  const payload = (await response.json().catch(() => null)) as
    | { text?: unknown; error?: unknown }
    | null
  if (!response.ok) {
    const error =
      typeof payload?.error === "string" && payload.error.trim()
        ? payload.error
        : `Leo request failed (${response.status})`
    throw new Error(error)
  }
  if (typeof payload?.text !== "string" || !payload.text.trim()) {
    throw new Error("Leo returned an empty answer")
  }
  return payload.text.trim()
}

export type LeoOpenRouterDraftPayload = {
  content?: string
  nextStep?: string
  reviewField?: string
  reviewFinding?: string
  reviewAction?: string
  reviewEvidence?: string
  type?: AuthoringQuestionType
  key?: string
  variants?: Array<{
    angle?: string
    leadIn?: string
    options?: Array<{ text?: string; isCorrect?: boolean }>
    rationale?: string
    bloom?: string
    difficulty?: string
    key?: string
    pairs?: Array<{ left?: string; right?: string }>
    orderedItems?: Array<{ text?: string }>
    numericValue?: string
    numericUnits?: string
    numericTolerance?: string
    fillBlankAnswers?: Array<{ accepted?: string }>
  }>
}

const CHOICE_DRAFT_TYPES = new Set<AuthoringQuestionType>([
  "mcq_single",
  "mcq_multiple",
  "true_false",
])

export function resolveLeoDraftTypeId(
  raw: string,
): AuthoringQuestionType | undefined {
  const id = raw.toLowerCase().replace(/\*/g, "").trim()
  const exact = AUTHORING_QUESTION_TYPES.find((row) => row.value === id)
  if (exact) return exact.value
  const text = id.replace(/_/g, " ").replace(/\s+/g, " ").trim()
  if (/multi[- ]?select|select all/.test(text)) return "mcq_multiple"
  if (/fill|blank/.test(text)) return "fill_blank"
  if (/true/.test(text) && /false/.test(text)) return "true_false"
  if (/short answer/.test(text)) return "short_answer"
  if (/numeric|calculat/.test(text)) return "numeric"
  if (/\bmatch/.test(text)) return "matching"
  if (/order|sequence/.test(text)) return "ordering"
  if (/\bessay\b/.test(text)) return "essay"
  if (/single|multiple choice|\bmcq\b/.test(text)) return "mcq_single"
  return undefined
}

function numericAnswer(raw: string | undefined): string | undefined {
  const value = raw?.trim()
  if (!value) return undefined
  if (/^-?\d+(?:\.\d+)?$/.test(value)) return value
  const fraction = value.match(/^(-?\d+)\s*\/\s*(\d+)$/)
  if (!fraction) return undefined
  const denominator = Number(fraction[2])
  if (denominator === 0) return undefined
  const quotient = Number(fraction[1]) / denominator
  return Number.isFinite(quotient) && Number.isInteger(quotient * 1_000_000)
    ? String(quotient)
    : undefined
}

/**
 * The placeholder strings a repair is normally asked to remove.
 *
 * Kept in step with `choiceSetIsBroken`, which is what flags them on
 * the question in the first place — if the two drifted, Leo could
 * "fix" a question into something still flagged.
 */
function isPlaceholderChoice(text: string | undefined): boolean {
  return choiceSetIsBroken([{ text }])
}

function mergeDraftOptions(
  type: AuthoringQuestionType,
  template: NonNullable<LeoQuestionDraftPatch["options"]> | undefined,
  incoming?: Array<{ text?: string; isCorrect?: boolean }>,
  key?: string,
): LeoQuestionDraftPatch["options"] {
  if (!CHOICE_DRAFT_TYPES.has(type)) return undefined
  if (type === "true_false") {
    const normalizedKey = key?.trim().replace(/[.!?]+$/, "").toLowerCase()
    const keyedAnswer = /^(true|a)$/.test(normalizedKey ?? "")
      ? "true"
      : /^(false|b)$/.test(normalizedKey ?? "")
        ? "false"
        : undefined
    const markedAnswer = incoming?.find((row) => row.isCorrect)?.text
      ?.trim()
      .toLowerCase()
    const answer =
      keyedAnswer ??
      (markedAnswer === "true" || markedAnswer === "false"
        ? markedAnswer
        : undefined) ??
      (template?.find((row) => row.isCorrect)?.text.trim().toLowerCase() ===
      "false"
        ? "false"
        : "true")
    return (["True", "False"] as const).map((text, index) => ({
      id: template?.[index]?.id ?? `opt-leo-${index + 1}`,
      text,
      isCorrect: text.toLowerCase() === answer,
      rationale: template?.[index]?.rationale ?? "",
    }))
  }
  /**
   * Drop the placeholders whatever the model says.
   *
   * "All of the above", "None of the above" and "the Nth stated
   * mechanism" are the exact strings a repair is usually asked to get
   * rid of, so returning them is the model failing the instruction. A
   * shorter set of real choices is a better answer than a full set with
   * the defect still in it — and if that empties the list, the template
   * below is used instead and the finding stays open, which is the
   * truth.
   */
  const present = incoming?.filter((row) => row.text?.trim()) ?? []
  const sameAsCurrent =
    template !== undefined &&
    present.length === template.length &&
    present.every(
      (row, index) =>
        row.text?.trim().toLowerCase() ===
        template[index]?.text.trim().toLowerCase(),
    )
  const source = sameAsCurrent
    ? present
    : present.filter((row) => !isPlaceholderChoice(row.text))
  const cap = type === "mcq_single" ? 4 : 8
  if (source.length > 0) {
    // Letter keys address positions in the model's own list. If a
    // placeholder was dropped the letters have shifted, so the key is
    // matched on text instead of pointing at whatever moved up.
    const droppedAny = present.length !== source.length
    const keyLetters =
      !droppedAny && key?.trim().match(/^[A-H](?:\s*,\s*[A-H])*$/i)
        ? key.toUpperCase().split(/\s*,\s*/)
        : []
    const keyLetterSet = new Set(keyLetters)
    const next = source.slice(0, cap).map((src, index) => ({
      id: template?.[index]?.id ?? `opt-leo-${index + 1}`,
      text: sanitizeLeoCopy(src.text!.trim()),
      isCorrect: keyLetterSet.size > 0
        ? keyLetterSet.has(String.fromCharCode(65 + index))
        : Boolean(src.isCorrect) || Boolean(key && src.text?.trim().toLowerCase() === key.toLowerCase()),
      rationale: template?.[index]?.rationale ?? "",
    }))
    return next
  }
  return template?.slice(0, cap)
}

function draftTypeFromPayload(
  fallback: AuthoringQuestionType,
  parsed: LeoOpenRouterDraftPayload,
): AuthoringQuestionType {
  const first = parsed.variants?.[0]
  const correct =
    first?.options?.filter((option) => option.isCorrect).length ?? 0
  if (parsed.type) {
    const labeled =
      AUTHORING_QUESTION_TYPES.some((row) => row.value === parsed.type)
        ? parsed.type
        : resolveLeoDraftTypeId(parsed.type)
    if (labeled) return labeled
  }
  if (correct > 1) return "mcq_multiple"
  const stem = first?.leadIn ?? ""
  if (/\{\{|___/.test(stem)) return "fill_blank"
  const optionTexts = (first?.options ?? [])
    .map((option) => option.text?.trim().toLowerCase())
    .filter(Boolean)
  if (
    optionTexts.length === 2 &&
    optionTexts.includes("true") &&
    optionTexts.includes("false")
  ) {
    return "true_false"
  }
  return fallback
}

export function mergeOpenRouterDraftVariants(
  type: AuthoringQuestionType,
  brief: LeoQuestionBrief,
  folderName: string | undefined,
  parsed: LeoOpenRouterDraftPayload,
): { content?: string; variants: LeoQuestionDraftVariant[] } | null {
  const textValue = (value: unknown): string | undefined =>
    typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined
  parsed = {
    ...parsed,
    key: textValue(parsed.key),
    variants: parsed.variants?.map((variant) => ({
      ...variant,
      key: textValue(variant.key),
      numericValue: textValue(variant.numericValue),
      numericTolerance: textValue(variant.numericTolerance),
      numericUnits: textValue(variant.numericUnits),
      options: variant.options?.map((option) =>
        typeof option === "string"
          ? { text: option, isCorrect: false }
          : { ...option, isCorrect: option.isCorrect === true },
      ),
      orderedItems: variant.orderedItems?.map((item) =>
        typeof item === "string" ? { text: item } : item,
      ),
      fillBlankAnswers: variant.fillBlankAnswers?.map((answer) =>
        typeof answer === "string" ? { accepted: answer } : answer,
      ),
    })),
  }
  const resolvedType = draftTypeFromPayload(type, parsed)
  const templates = buildLeoQuestionDraftVariants(resolvedType, brief, folderName)
  const incoming = (parsed.variants ?? []).filter(
    (variant) => variant.leadIn?.trim(),
  )
  if (incoming.length === 0) return null
  if (incoming.length > 1 && incoming.slice(1).some((variant) =>
    !mergeOpenRouterDraftVariants(resolvedType, brief, folderName, {
      ...parsed, type: resolvedType, variants: [variant],
    })
  )) return null
  if (resolvedType === "hotspot") return null
  const first = incoming[0]!
  const normalizedBloom = typeof first.bloom === "string"
    ? (["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"].find((level) => level.toLowerCase() === first.bloom?.trim().toLowerCase()))
    : undefined
  const proposedBloom = normalizedBloom || brief.bloom
  if (/\b(?:prioriti[sz]e|priorities|which action|first action|what should (?:the )?nurse do|appropriate nursing actions)\b/i.test(first.leadIn ?? "") &&
      (proposedBloom === "Remember" || proposedBloom === "Understand")) return null
  const firstKey = first.key?.trim() || parsed.key?.trim() || ""
  const firstOptions = first.options?.filter((row) => row.text?.trim()) ?? []
  if (resolvedType === "mcq_single" && (firstOptions.length < 3 || firstOptions.length > 4)) return null
  if (resolvedType === "mcq_multiple" && firstOptions.length < 3) return null
  if ((resolvedType === "mcq_single" || resolvedType === "mcq_multiple") &&
      !firstKey && !firstOptions.some((row) => row.isCorrect)) return null
  if (resolvedType === "mcq_single" || resolvedType === "mcq_multiple") {
    const mergedOptions = mergeDraftOptions(resolvedType, undefined, firstOptions, firstKey) ?? []
    const keyed = mergedOptions.filter((row) => row.isCorrect).length
    if (resolvedType === "mcq_single" && keyed !== 1) return null
    if (resolvedType === "mcq_multiple" && (keyed < 2 || keyed >= firstOptions.length)) return null
    const stem = first.leadIn?.toLowerCase() ?? ""
    const acuteRisk = /\b(?:escalat\w*|immediate|urgent|deteriorat\w*|unstable)\b/.test(stem) &&
      (/\bheart rate (?:of )?(?:1[2-9]\d|2\d\d)\b/.test(stem) ||
       /\bblood pressure (?:of )?(?:[1-8]?\d|90)\/\d+\b/.test(stem) ||
       /\brespiratory rate (?:of )?(?:2[8-9]|[3-9]\d)\b/.test(stem))
    const newChestPain = /\b(?:new|sudden\w*|acute|unstable)\b/.test(stem) && /\bchest pain\b/.test(stem)
    const asksForUnsafeAction = /\b(?:avoid|least appropriate|inappropriate|unsafe|should not)\b/.test(stem)
    if ((acuteRisk || newChestPain) && !asksForUnsafeAction && mergedOptions.some((row) => row.isCorrect &&
      /\b(?:discharg\w*|send (?:the patient )?home|reassur\w*|wait\w*|delay\w*)\b/i.test(row.text))) return null
    const glucose = stem.match(/\b(?:blood\s+)?glucose(?:\s+level)?(?:\s+of)?\s+(\d{1,3})\s*(?:mg\/dl)?\b/)
    if (glucose && Number(glucose[1]) < 54 && /\b(?:first|priority|immediate)\b/.test(stem) &&
      mergedOptions.some((row) => row.isCorrect && /\b(?:check\s+(?:the\s+)?(?:blood\s+)?glucose|assess.{0,30}vital\s+signs)\b/i.test(row.text))) return null
  }
  if ((resolvedType === "true_false") &&
      !/^(true|false|a|b)[.!?]?$/i.test(firstKey) &&
      !firstOptions.some((row) => row.isCorrect)) return null
  if ((resolvedType === "short_answer" || resolvedType === "essay") &&
      !firstKey && !first.rationale?.trim()) return null
  if (resolvedType === "fill_blank" &&
      !firstKey && !first.fillBlankAnswers?.some((row) => row.accepted?.trim()) &&
      !/\{\{(?!blank\}\})[^}]+\}\}/i.test(first.leadIn ?? "")) return null
  if (resolvedType === "fill_blank" && !/\{\{[^}]+\}\}|___/.test(first.leadIn ?? "")) return null
  if (resolvedType === "fill_blank" &&
      ((first.leadIn?.match(/\{\{[^}]+\}\}|___/g) ?? []).length !== 1)) return null
  if (resolvedType === "numeric" &&
      !numericAnswer(first.numericValue?.trim() || firstKey)) return null
  if (resolvedType === "matching" &&
      ((first.pairs?.filter((row) => row.left?.trim() && row.right?.trim()).length ?? 0) < 2 ||
       (first.pairs?.length ?? 0) > 6 ||
       first.pairs?.some((row) => /^(?:first|second|third|fourth|fifth|sixth|last)\s+(?:action|step|priority)$|^(?:priority|step|action)\s*[1-6]$/i.test(row.right?.trim() ?? "")) ||
       first.pairs?.some((row) => /^(?:step|description|item|answer)\s*(?:\d+|[A-Z])\b/i.test(row.left?.trim() ?? "") ||
         /^(?:step|description|item|answer)\s*(?:\d+|[A-Z])\b/i.test(row.right?.trim() ?? "")))) return null
  if (resolvedType === "ordering" &&
      ((first.orderedItems?.filter((row) => row.text?.trim()).length ?? 0) < 2 ||
       (first.orderedItems?.length ?? 0) > 6)) return null

  const variants = incoming.map((src, index) => {
    const template = templates[0]!
    const leadIn = src.leadIn?.trim()
    const key = (src.key ?? parsed.key)?.trim()
    const embeddedBlank = src.leadIn?.match(/\{\{(?!blank\}\})([^}]+)\}\}/i)?.[1]?.trim()
    const blankAnswers = src.fillBlankAnswers?.filter((row) => row.accepted?.trim()) ?? []
    const accepted = (key || blankAnswers[0]?.accepted?.trim() || embeddedBlank)?.replace(/[.!?]+$/, "")
    const normalizedLeadIn = resolvedType === "fill_blank"
      ? (leadIn ?? "").replace(/\{\{[^}]+\}\}|___/g, "{{blank}}")
      : leadIn
    const options = mergeDraftOptions(
      resolvedType,
      template.patch.options,
      src.options,
      key,
    )
    const reviewField = parsed.reviewField?.trim().toLowerCase()
    const reviewFinding = parsed.reviewFinding?.trim()
    const reviewAction = parsed.reviewAction?.trim()
    const reviewEvidence = parsed.reviewEvidence?.trim().replace(/^["“”']|["“”']$/g, "").toLowerCase()
    const reviewSource = [leadIn, key, src.rationale, folderName,
      ...(src.options ?? []).map((row) => row.text),
      ...(src.pairs ?? []).flatMap((row) => [row.left, row.right]),
      ...(src.orderedItems ?? []).map((row) => row.text),
    ].filter(Boolean).join(" ").toLowerCase()
    const spottedReview =
      reviewField &&
      /^(stem|choices|rationale|format|bloom|course|answer)$/.test(reviewField) &&
      reviewEvidence && reviewEvidence.length >= 6 && reviewSource.includes(reviewEvidence) &&
      reviewFinding && reviewFinding.length >= 20 && reviewFinding.length <= 170 &&
      reviewFinding.toLowerCase().includes(reviewEvidence) &&
      reviewAction && reviewAction.length >= 12 && reviewAction.length <= 170
      && !(/^(?:choices)$/.test(reviewField) && /replace.+(?:with|by) (?:a |the )?correct (?:choice|answer|step)/i.test(reviewAction))
        ? {
            field: reviewField,
            finding: sanitizeLeoCopy(reviewFinding),
            action: sanitizeLeoCopy(reviewAction),
          }
        : undefined
    return {
      ...template,
      id: `leo-variant-${index + 1}`,
      spottedReview,
      angle: src.angle?.trim() ? sanitizeLeoCopy(src.angle) : template.angle,
      patch: {
        ...template.patch,
        type: resolvedType,
        leadIn: normalizedLeadIn ? sanitizeLeoCopy(normalizedLeadIn) : template.patch.leadIn,
        options,
        fillBlankAnswers:
          resolvedType === "fill_blank" && accepted
            ? [
                {
                  id: template.patch.fillBlankAnswers?.[0]?.id ?? "blk-leo-1",
                  accepted: sanitizeLeoCopy(accepted),
                },
              ]
            : undefined,
        numericValue:
          resolvedType === "numeric" ? numericAnswer(src.numericValue?.trim() || key) : undefined,
        numericUnits: resolvedType === "numeric" && src.numericUnits?.trim().toLowerCase() !== "none"
          ? src.numericUnits?.trim() : undefined,
        numericTolerance: resolvedType === "numeric" && /^\d+(?:\.\d+)?$/.test(src.numericTolerance?.trim() ?? "")
          ? src.numericTolerance?.trim() : undefined,
        pairs: resolvedType === "matching"
          ? src.pairs?.filter((row) => row.left?.trim() && row.right?.trim()).map((row, pairIndex) => ({
              id: template.patch.pairs?.[pairIndex]?.id ?? `pair-leo-${pairIndex + 1}`,
              left: sanitizeLeoCopy(row.left!), right: sanitizeLeoCopy(row.right!),
            }))
          : undefined,
        orderedItems: resolvedType === "ordering"
          ? src.orderedItems?.filter((row) => row.text?.trim()).map((row, itemIndex) => ({
              id: template.patch.orderedItems?.[itemIndex]?.id ?? `ord-leo-${itemIndex + 1}`,
              text: sanitizeLeoCopy(row.text!),
            }))
          : undefined,
        rationale: src.rationale?.trim()
          ? sanitizeLeoCopy(src.rationale)
          : key && (resolvedType === "short_answer" || resolvedType === "essay")
            ? `Model answer: ${sanitizeLeoCopy(key)}`
            : "",
        bloom: typeof src.bloom === "string"
          ? ["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"].find((level) => level.toLowerCase() === src.bloom?.trim().toLowerCase()) ?? template.patch.bloom
          : template.patch.bloom,
        difficulty: typeof src.difficulty === "string" && /^(easy|medium|hard)$/i.test(src.difficulty.trim())
          ? src.difficulty.trim().toLowerCase() as "easy" | "medium" | "hard" : template.patch.difficulty,
        title: leadIn
          ? questionTitleFromLeadIn(leadIn)
          : template.patch.title,
      },
    }
  })

  const content = parsed.content?.trim()
  return {
    content: content ? sanitizeLeoCopy(content) : undefined,
    variants,
  }
}

export function leoDraftSystemPrompt(input: {
  typeLabel: string
  typeId?: AuthoringQuestionType
  kind: "create-draft" | "choices" | "option"
  letter?: string
}): string {
  const locked =
    input.kind === "create-draft" && input.typeId
      ? `Write one complete exam item as ${input.typeLabel} only. First line must be TYPE: ${input.typeId}. Do not write a single-choice item unless that type id is mcq_single.`
      : "Write one complete exam item. Choose the type that best assesses the objective: multi-select, true or false, fill in the blank, short answer, numeric, matching, ordering, or single choice. Do not default to single choice."
  const focus =
    input.kind === "choices"
      ? "Keep the stem. Write one new choice set only."
      : input.kind === "option"
        ? `Rewrite option ${input.letter ?? "A"} once. Keep the stem and the other choices.`
        : locked
  return [
    LEO_GENERIC_SYSTEM,
    focus,
    "Write a real exam item a student would answer. Choice, true or false, short-answer, and numeric stems should end with a question. A fill-in-the-blank stem must contain {{blank}}. Matching and ordering stems should give a clear task; essay stems may give a focused explanation task.",
    "Do not write an assignment, monitoring plan, or lesson plan.",
    "If a current stem and key are provided, write a new stem for that question. Keep its question type and keyed answer unless faculty explicitly asks to change the type. Do not copy or lightly paraphrase the current stem.",
    "Difficulty changes the evidence, distractor plausibility, and reasoning needed; it does not determine the question type. Low difficulty does not mean true or false. Choose a format that can actually test the objective, and prefer a case with a decision when the objective asks learners to interpret findings or decide when to act. Never replace that objective with a broad always-or-never statement.",
    "When faculty asks to make an item harder, increase the reasoning needed by adding relevant case evidence or a closer judgment. Do not merely reverse a statement, add always or never, or relabel the difficulty.",
    "For clinical priority or sequencing items, supply enough patient findings to justify one defensible key. Do not invent a first-to-last treatment sequence from a vague severity label. Check urgent assessment and monitoring needs before assigning medication or referral priorities. Explain the order in the rationale; if the available case cannot support a unique order, choose a different item structure.",
    "Start with TYPE: then the authoring type id (mcq_single, mcq_multiple, true_false, fill_blank, short_answer, essay, numeric, matching, or ordering).",
    "Then STEM:. For single choice add 4 distinct choices and one keyed letter. For multi-select add 4 distinct choices, at least two keyed and at least one incorrect, then KEY: A, B (one letter per choice, separated by commas; never AB or AD). For true or false add KEY: True or KEY: False. For fill in the blank put exactly one {{blank}} in the stem and KEY: accepted text without final punctuation. For short answer or essay add KEY: a model answer that demonstrates the reasoning asked for. For numeric add KEY: a decimal number; include UNITS: only if units apply and TOLERANCE: only if a numeric tolerance is needed. Never write None for either. For matching add 2 to 6 lines PAIR 1: specific prompt => its correct specific match, never abstract Step 1 or Description A labels. If the task is to put actions in first-to-last sequence, use ordering rather than matching. For ordering add 2 to 6 lines ORDER 1: first step, ORDER 2: second step in correct sequence. Always add RATIONALE: explaining the key or each pairing. Add BLOOM: Remember, Understand, Apply, Analyze, Evaluate, or Create based on what the student actually does. Add DIFFICULTY: easy, medium, or hard based on evidence and reasoning, not format.",
    "Fit the live product, program, and category.",
    "After writing the item, compare the stem with its key or model answer. Check whether the key demonstrates the reasoning the stem asks for. Then review answer cues, ambiguity, weak distractors, missing case context, and format fit. Always end with REVIEW_FIELD: one of stem, choices, rationale, format, bloom, course, answer, or none. For a concrete issue, also add REVIEW_EVIDENCE: an exact six-or-more-character excerpt from the stem, key, choices, or course; REVIEW_FINDING: one specific faculty-facing observation that repeats that excerpt; REVIEW_ACTION: one concrete edit Leo can make; NEXT_STEP: the same finding and action in one short sentence. A distractor is meant to be incorrect; suggest making it plausible, never replacing it with another correct answer just because it is wrong. If no meaningful issue exists, write REVIEW_FIELD: none and omit the other review lines. Do not ask for generic specificity.",
  ].join(" ")
}

const LABELED_OPTION = /^[ \t]*\**(?:Choice\s+)?([A-Ha-h])[\).\:]\**\s+(.+?)\**\s*$/i

export function parseLeoLabeledDraft(text: string): LeoOpenRouterDraftPayload | null {
  const lines = text.replace(/\r/g, "").split("\n")
  let leadIn = ""
  const options: Array<{ text: string; isCorrect: boolean }> = []
  let key = ""
  let nextStep = ""
  let reviewField = ""
  let reviewFinding = ""
  let reviewAction = ""
  let reviewEvidence = ""
  let rationale = ""
  let bloom = ""
  let difficulty = ""
  let numericUnits = ""
  let numericTolerance = ""
  const pairs: Array<{ left: string; right: string }> = []
  const orderedItems: Array<{ text: string }> = []
  let labeledType: AuthoringQuestionType | undefined
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    const typeLine = line.match(/^(?:\*\*)?TYPE(?:\*\*)?:\s*(.+)$/i)
    if (typeLine?.[1]) {
      const next = resolveLeoDraftTypeId(typeLine[1].replace(/\*+/g, "").trim())
      if (next) labeledType = next
      continue
    }
    const stem = line.match(/^(?:\*\*)?STEM(?:\*\*)?:\s*(.+)$/i)
    if (stem?.[1]) {
      leadIn = stem[1].replace(/\*+/g, "").trim()
      continue
    }
    const keyLine = line.match(/^(?:\*\*)?KEY(?:\*\*)?:\s*(.+)$/i)
    if (keyLine?.[1]) {
      key = keyLine[1].replace(/\*+/g, "").trim()
      continue
    }
    const rationaleLine = line.match(/^(?:\*\*)?RATIONALE(?:\*\*)?:\s*(.+)$/i)
    if (rationaleLine?.[1]) { rationale = rationaleLine[1].replace(/\*+/g, "").trim(); continue }
    const bloomLine = line.match(/^(?:\*\*)?BLOOM(?:\*\*)?:\s*(.+)$/i)
    if (bloomLine?.[1]) { bloom = bloomLine[1].replace(/\*+/g, "").trim(); continue }
    const difficultyLine = line.match(/^(?:\*\*)?DIFFICULTY(?:\*\*)?:\s*(.+)$/i)
    if (difficultyLine?.[1]) { difficulty = difficultyLine[1].replace(/\*+/g, "").trim().toLowerCase(); continue }
    const unitsLine = line.match(/^(?:\*\*)?UNITS(?:\*\*)?:\s*(.+)$/i)
    if (unitsLine?.[1]) { numericUnits = unitsLine[1].replace(/\*+/g, "").trim(); continue }
    const toleranceLine = line.match(/^(?:\*\*)?TOLERANCE(?:\*\*)?:\s*(.+)$/i)
    if (toleranceLine?.[1]) { numericTolerance = toleranceLine[1].replace(/\*+/g, "").trim(); continue }
    const pairLine = line.match(/^PAIR\s+\d+\s*:\s*(.+?)\s*(?:=>|→)\s*(.+)$/i)
    if (pairLine?.[1] && pairLine[2]) {
      pairs.push({ left: sanitizeLeoCopy(pairLine[1]), right: sanitizeLeoCopy(pairLine[2]) })
      continue
    }
    const orderLine = line.match(/^(?:ORDER\s+)?\d+[.):]\s*(.+)$/i)
    if (orderLine?.[1]) { orderedItems.push({ text: sanitizeLeoCopy(orderLine[1]) }); continue }
    const improvement = line.match(/^(?:\*\*)?NEXT_STEP(?:\*\*)?:\s*(.+)$/i)
    if (improvement?.[1]) {
      nextStep = improvement[1].replace(/\*+/g, "").trim()
      continue
    }
    const review = line.match(/^(?:\*\*)?REVIEW_(FIELD|EVIDENCE|FINDING|ACTION)(?:\*\*)?:\s*(.+)$/i)
    if (review?.[1] && review[2]) {
      const value = review[2].replace(/\*+/g, "").trim()
      if (review[1].toUpperCase() === "FIELD") reviewField = value
      if (review[1].toUpperCase() === "EVIDENCE") reviewEvidence = value
      if (review[1].toUpperCase() === "FINDING") reviewFinding = value
      if (review[1].toUpperCase() === "ACTION") reviewAction = value
      continue
    }
    const inlineChoices = line.match(/^(?:\*\*)?(?:CHOICES|OPTIONS)(?:\*\*)?:\s*(.+)$/i)
    if (inlineChoices?.[1]) {
      const entries = [...inlineChoices[1].matchAll(/(?:^|[,;]\s*)([A-H])[.):]\s*(.*?)(?=(?:[,;]\s*[A-H][.):])|$)/gi)]
      if (entries.length >= 2) {
        options.push(...entries.map((entry) => ({
          text: sanitizeLeoCopy(entry[2]!.replace(/\*+/g, "").trim()),
          isCorrect: false,
        })))
      }
      continue
    }
    const option = line.match(LABELED_OPTION)
    if (option?.[1] && option[2]) {
      options.push({
        text: sanitizeLeoCopy(option[2].replace(/\*+/g, "").trim()),
        isCorrect: false,
      })
    }
  }
  if (!leadIn && options.length === 0) return null
  const keyLetters = [...key.matchAll(/\b([A-F])\b/gi)].map((match) => match[1]!)
  const letterKey =
    keyLetters.length > 0 &&
    /^[A-F](?:\s*,\s*[A-F])*$/i.test(key.trim())
  if (letterKey && options.length > 0) {
    for (const letter of keyLetters) {
      const index = letter.toUpperCase().charCodeAt(0) - 65
      if (options[index]) options[index] = { ...options[index]!, isCorrect: true }
    }
    if (keyLetters.length > 1) labeledType = "mcq_multiple"
  }
  return {
    type: labeledType,
    key: key || undefined,
    nextStep: nextStep || undefined,
    reviewField: reviewField || undefined,
    reviewFinding: reviewFinding || undefined,
    reviewAction: reviewAction || undefined,
    reviewEvidence: reviewEvidence || undefined,
    variants: [
      {
        angle: "From this ask",
        leadIn: leadIn ? sanitizeLeoCopy(leadIn) : undefined,
        options,
        key: key || undefined,
        rationale: rationale || undefined,
        bloom: bloom || undefined,
        difficulty: difficulty || undefined,
        numericUnits: numericUnits || undefined,
        numericTolerance: numericTolerance || undefined,
        pairs: pairs.length ? pairs : undefined,
        orderedItems: orderedItems.length ? orderedItems : undefined,
      },
    ],
  }
}
